import { RequireExactlyOne } from 'type-fest';

import {
  ApplyOnceStatus,
  Job,
  OutputKind,
  PostVisit,
  PrevisitData,
  ProblemGroup,
  ScenarioRule,
} from '../scenario';
import { Sex, User } from '../user';
import { Guarantor } from '../patient/guarantor';
import { BaseInsuranceInformation } from '../patient/insuranceInformation';
import { Entity } from '../search';
import { Specialty } from '../../index';
import { EncounterPayment } from '../payment';
import { AppointmentStatus, SchedulingSource } from '../encounter';
import { AuthorizationStatus } from '../patient/order';

export { OutputKind };

interface Feed {
  feedId: string;
  addedTime: Date;
}

export interface AppliedEntity {
  id: string;
  datetime: Date;
  status: ApplyOnceStatus | null;
}

interface JobStatus {
  job: Job;
  status: 'SUCCESS' | 'MANUAL' | 'FAILED';
}

interface BaseFingerprint {
  ehrInformationId: string;
  feeds: Feed[];
  practiceId: string;
  practiceGroupId: string;
  organizationId: string;
  timezone: string;
  previsit: PrevisitData | null;
  guarantors: Guarantor[];
  dateOfBirth: string;
  sex: Sex | null;
  patientProblems: object[] | null;
  enrolledInEhrPortal: boolean | null;
  homeMarket: string | null;
  isValueBasedCare: boolean;
  patientAdvisories: string[] | null;
  insurances: Pick<BaseInsuranceInformation, 'payor' | 'primary'>[];
  completedJobs: JobStatus[];
  completedEvents: Event[];
  previsitLinkId: string | null;
  userAgent: string | null;
  documents: AppliedEntity[];
  ehrLogins: AppliedEntity[];
  forms: AppliedEntity[];
  orders: AppliedEntity[];
  visits: AppliedEntity[];
}

interface PatientFingerprint extends BaseFingerprint {
  entity: Entity.Patient;
}

export interface EncounterFingerprint extends BaseFingerprint {
  encounterId: string;
  entity: Entity.Encounter;
  ehrReferenceValue: string | null;
  provider: Pick<User, 'id' | 'kind'>;
  specialty: Specialty;
  startTime: Date;
  standardVisitReason: string | null;
  appointmentNote: string | null;
  procedures: string[] | null;
  insuranceProviderName: string | null;
  scenarioPostVisitId: string | null;
  payment: EncounterPayment | null;
  schedulingSource: SchedulingSource;
  encounters: AppliedEntity[];
  appointmentStatus: AppointmentStatus | null;
  preregistrationCompletedExternally: boolean;
}

interface OrderFingerprint extends BaseFingerprint {
  patientOrderId: string;
  entity: Entity.Order;
  startTime: Date;
  specialty: string;
  insuranceProviderName: string | null;
  procedures: string[] | null;
  referredByProvider: Pick<User, 'id'>;
  referredToProvider: Pick<User, 'id'> | null;
}

export type Fingerprint =
  | PatientFingerprint
  | EncounterFingerprint
  | OrderFingerprint;

interface Authorization {
  status: AuthorizationStatus;
  note?: string | null;
}

interface FormTemplate {
  templateId: string;
  add: boolean;
  refreshTimestamp?: string | null;
}

export interface JobWithRefresh {
  job: Job;
  refreshTimestamp?: string | null;
}

export type Notification = {
  id: string;
  windowStartTime: string | null;
  windowEndTime: string | null;
};

export type Conjecture = Partial<{
  authorizations: Authorization[] | null;
  forms: FormTemplate[];
  jobs: JobWithRefresh[];
  noteTemplateIds: string[];
  notifications: Notification[];
  patientProblems: string[];
  postVisits: PostVisit[];
  preRegistrations: PostVisit[];
  problemGroups: ProblemGroup[];
  scenarioIds: string[];
  scheduledTimes: string[];
}>;

export type EntityId = RequireExactlyOne<{
  encounterId: string;
  ehrInformationId: string;
  patientOrderId: string;
}>;

export type SearchId = RequireExactlyOne<{
  encounterId: string;
  ehrInformationId: string;
  patientOrderId: string;
  eventId: string;
}>;

export interface Rule {
  rule: {
    name: keyof ScenarioRule;
    value: ScenarioRule[keyof ScenarioRule];
    exists: boolean;
  }[];
  fingerprint: {
    name: keyof Fingerprint;
    value: Fingerprint[keyof Fingerprint];
  }[];
  match: boolean;
}

export interface RuleGroup {
  rules: Rule[];
  name: string;
}
